import {
  FlexBox,
  space,
  theme,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import type { ActiveProgram as ActiveProgramType, Unit } from "../view-model";

const ProgramWrapper = styledComponents.div`
  background: ${theme.colorPalette.grey100};
  border-radius: 8px;
  padding: ${space(3)} ${space(4)};
`;

const ValueText = styledComponents.span`
  font-weight: 600;
  color: ${theme.colorPalette.blue500};
`;

const ProgressBarOuter = styledComponents.div`
  width: 100%;
  height: 8px;
  background: ${theme.colorPalette.grey300};
  border-radius: 4px;
  margin-top: ${space(2)};
  overflow: hidden;
`;

const ProgressBarInner = styledComponents.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => Math.min(100, Math.max(0, $progress))}%;
  background: ${theme.colorPalette.blue500};
  border-radius: 4px;
  transition: width 0.5s ease;
`;

const InfoText = styledComponents.span`
  font-size: 12px;
  color: ${theme.colorPalette.grey600};
  margin-left: auto;
`;

function formatRuntime(ms?: number): string {
  if (ms === undefined || ms <= 0) return "";
  const totalSeconds = Math.floor(ms / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

type ActiveProgramProps = {
  activeProgram: ActiveProgramType;
  unitState: Unit["state"];
};

export const ActiveProgramDisplay: FC<ActiveProgramProps> = ({
  activeProgram,
  unitState,
}) => {
  const { t } = useTranslation();
  const {
    currentProgramTemplate,
    currentRuntime,
    currentStepName,
    currentStepNumber,
    estimatedRuntime,
    estimatedStepNumbers,
  } = activeProgram;

  // Calculate progress: step-based > runtime-based (only if values are meaningful)
  const displayProgress =
    (estimatedStepNumbers &&
    estimatedStepNumbers > 0 &&
    currentStepNumber &&
    currentStepNumber > 0
      ? Math.round((currentStepNumber / estimatedStepNumbers) * 100)
      : undefined) ??
    (estimatedRuntime &&
    estimatedRuntime > 0 &&
    currentRuntime &&
    currentRuntime > 0
      ? Math.round((currentRuntime / estimatedRuntime) * 100)
      : undefined);

  let runtimeText: string | undefined;
  if (
    currentRuntime !== undefined &&
    currentRuntime > 0 &&
    estimatedRuntime !== undefined &&
    estimatedRuntime > 0
  ) {
    runtimeText = `${formatRuntime(currentRuntime)} / ${formatRuntime(
      estimatedRuntime
    )}`;
  } else if (currentRuntime !== undefined && currentRuntime > 0) {
    runtimeText = formatRuntime(currentRuntime);
  }

  const isRunning = unitState === "running";
  const stepDescription =
    isRunning && currentStepName ? ` - ${currentStepName}` : "";
  let stepText: string | undefined;
  if (
    currentStepNumber !== undefined &&
    currentStepNumber > 0 &&
    estimatedStepNumbers !== undefined &&
    estimatedStepNumbers > 0
  ) {
    stepText = `${currentStepNumber}/${estimatedStepNumbers}${stepDescription}`;
  } else if (currentStepNumber !== undefined && currentStepNumber > 0) {
    stepText = `${currentStepNumber}${stepDescription}`;
  }

  return (
    <ProgramWrapper>
      <FlexBox alignItems="center" gap={space(2)}>
        <span>{t("detail.program.title")}:</span>
        <ValueText>{currentProgramTemplate || "–"}</ValueText>
        {stepText && <InfoText>{stepText}</InfoText>}
        {runtimeText && <InfoText>{runtimeText}</InfoText>}
      </FlexBox>
      {displayProgress !== undefined && displayProgress > 0 && (
        <ProgressBarOuter>
          <ProgressBarInner $progress={displayProgress} />
        </ProgressBarOuter>
      )}
    </ProgramWrapper>
  );
};
